'use client';

import 'swiper/css';

import { FractionPagination } from '@fastfive-dev/fastfive-design-system/components';
import Image from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperClass } from 'swiper/types';

import { $api } from '@shared/apis';
import type { components } from '@shared/apis/fastfive';
import { GTM_EVENT } from '@/shared/constants/gtm';
import { gtmClick } from '@/shared/utils/gtm';
import { useBodyScrollLock } from '@publishing/hooks/useBodyScrollLock';
import { useMediaQuery } from '@publishing/hooks/useMediaQuery';

const BANNER_TYPE_CODE = 'MAIN_PAGE_MOBILE_POPUP';
const DISMISSED_STORAGE_KEY = `banner-popup-dismissed:${BANNER_TYPE_CODE}`;
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

type BannerInfo = components['schemas']['BannerInfoDetail'];
type BannerType = components['schemas']['BannerTypeDetail'];
type BannerTypeNode = Omit<BannerType, 'code'> & {
  code: string;
  children?: BannerTypeNode[];
};

function flattenBannerTypes(types: BannerTypeNode[]): BannerTypeNode[] {
  return types.flatMap((type) => [
    type,
    ...flattenBannerTypes(type.children ?? []),
  ]);
}

function getToday() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const date = String(now.getDate()).padStart(2, '0');

  return `${now.getFullYear()}-${month}-${date}`;
}

function isDismissedToday() {
  try {
    return localStorage.getItem(DISMISSED_STORAGE_KEY) === getToday();
  } catch {
    return false;
  }
}

function dismissToday() {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, getToday());
  } catch {
    // localStorage를 사용할 수 없어도 현재 팝업은 닫는다.
  }
}

function getBannerName(banner: BannerInfo) {
  return banner.banner.name ?? banner.subTitle ?? '배너';
}

function MobilePopupSlide({ banner }: { banner: BannerInfo }) {
  const imageUrl = banner.webImageUrl ?? '';
  const href = banner.banner.webLink;
  const textColor = banner.textColor ?? '#FFFFFF';

  const content = (
    <div
      className="flex h-[260px] w-full flex-col items-center justify-between overflow-hidden"
      style={{ backgroundColor: banner.backgroundColor ?? '#0B1756' }}
    >
      <div className="flex w-full flex-col items-center p-20 text-center leading-[1.5] tracking-[0]">
        {banner.mainTitle && (
          <p
            className="w-full text-[16px] font-medium opacity-75"
            style={{ color: textColor }}
          >
            {banner.mainTitle}
          </p>
        )}
        <p
          className="w-full text-[26px] font-bold"
          style={{ color: textColor }}
        >
          {banner.subTitle ?? banner.banner.name}
        </p>
      </div>

      <div className="relative h-[155px] w-[291px] shrink-0 overflow-hidden">
        <Image
          src={imageUrl}
          fill
          unoptimized
          alt={getBannerName(banner)}
          sizes="291px"
          className="object-contain object-center"
        />
      </div>
    </div>
  );

  if (!href) return content;

  return (
    <a
      href={href}
      className="block h-full w-full"
      {...gtmClick(GTM_EVENT.POPUP, getBannerName(banner), href)}
    >
      {content}
    </a>
  );
}

export function MobilePopupBanner() {
  const isMobile = useMediaQuery('(max-width: 1024px)', false);
  const [closed, setClosed] = useState(
    () => typeof window !== 'undefined' && isDismissedToday(),
  );
  const [currentSlide, setCurrentSlide] = useState(1);
  const dialogRef = useRef<HTMLDivElement>(null);

  const bannerTypesQuery = $api.useQuery(
    'get',
    '/api/v1/banner-types',
    {},
    { enabled: isMobile, retry: false },
  );

  const bannerTypeId = useMemo(
    () =>
      flattenBannerTypes(
        (bannerTypesQuery.data?.types ?? []) as BannerTypeNode[],
      ).find((type) => type.code === BANNER_TYPE_CODE)?.id,
    [bannerTypesQuery.data?.types],
  );

  const bannersQuery = $api.useQuery(
    'get',
    '/api/v1/banners',
    {
      params: {
        query: bannerTypeId !== undefined ? { bannerTypeId } : {},
      },
    },
    {
      enabled: isMobile && bannerTypeId !== undefined,
      retry: false,
    },
  );

  const banners = useMemo(
    () =>
      bannerTypeId !== undefined
        ? (bannersQuery.data?.banners ?? []).filter(
            (banner) =>
              banner.bannerTypeId === bannerTypeId &&
              Boolean(banner.webImageUrl),
          )
        : [],
    [bannerTypeId, bannersQuery.data?.banners],
  );

  const visible = isMobile && !closed && banners.length > 0;
  const hasMultipleBanners = banners.length > 1;

  useBodyScrollLock(visible);

  useEffect(() => {
    if (!visible) return;

    const previousFocus = document.activeElement as HTMLElement | null;
    const focusFrame = requestAnimationFrame(() => {
      dialogRef.current
        ?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
        ?.focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        setClosed(true);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusables = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ??
          [],
      );
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown, true);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[52] flex items-end">
      <div
        className="absolute inset-0 bg-gray-900/50"
        aria-hidden="true"
        onClick={() => setClosed(true)}
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="프로모션 배너"
        className="rounded-t-24 relative z-[1] flex w-full flex-col overflow-hidden bg-gray-100"
      >
        <div className="relative h-[260px] w-full overflow-hidden">
          {hasMultipleBanners && (
            <FractionPagination
              currentPage={currentSlide}
              totalPages={banners.length}
              className="absolute right-16 bottom-16 z-10"
            />
          )}

          <Swiper
            modules={[Autoplay]}
            className="h-full w-full"
            spaceBetween={0}
            slidesPerView={1}
            loop={hasMultipleBanners}
            autoplay={
              hasMultipleBanners
                ? { delay: 5000, disableOnInteraction: false }
                : false
            }
            onSlideChange={(swiper: SwiperClass) => {
              setCurrentSlide(swiper.realIndex + 1);
            }}
          >
            {banners.map((banner) => (
              <SwiperSlide key={banner.bannerInfoId} className="h-full w-full">
                <MobilePopupSlide banner={banner} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className="flex h-56 w-full shrink-0 items-center justify-between border-t border-gray-400 bg-gray-100 px-20 py-14 text-[14px] leading-[1.5] tracking-[0]">
          <button
            type="button"
            className="font-medium text-gray-700"
            onClick={() => {
              dismissToday();
              setClosed(true);
            }}
          >
            오늘 하루 보지 않기
          </button>
          <button
            type="button"
            className="font-bold text-gray-900"
            onClick={() => setClosed(true)}
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
